/**
 * ════════════════════════════════════════════════════════════════════════════
 * CMS_BLOCK_REGISTRY — Single Source of Truth for CMS Block Types
 * ════════════════════════════════════════════════════════════════════════════
 * 
 * This file is the ONLY place where CMS block types are defined.
 * All other files (BlockRenderer, cmsOverlayService, pageRegistry,
 * AdminPageBlocks) MUST derive their block lists from this registry.
 * 
 * ADDING A NEW BLOCK TYPE:
 * 1. Add entry to REGISTRY below
 * 2. Create React component in /components/blocks/
 * 3. Done. No other file needs modification.
 * 
 * SCHEMA:
 *   type: string         — unique block type identifier
 *   label: string        — admin-facing display name (French)
 *   component: React     — React component for rendering (lazy import)
 *   icon: string         — lucide-react icon name for admin UI
 *   allowedPageTypes: [] — page types where this block can appear
 *   systemOnly: boolean  — if true, managed by system, not CMS admin
 *   adminVisible: boolean — if true, appears in admin block type dropdown
 * 
 * PAGE TYPES: HUB, LISTING, CMS_CONTENT, PUBLIC, CUSTOM, FUNCTIONAL
 */

// ═══════════════════════════════════════════════════
// PAGE TYPE CONSTANTS (mirrored from pageRegistry)
// ═══════════════════════════════════════════════════
export const CMS_PAGE_TYPES = {
  HUB: 'HUB',
  LISTING: 'LISTING',
  CMS_CONTENT: 'CMS_CONTENT',
  PUBLIC: 'PUBLIC',
  CUSTOM: 'CUSTOM',
  FUNCTIONAL: 'FUNCTIONAL',
};

// Shorthand sets for common permission patterns
const ALL_CONTENT_PAGES = [
  CMS_PAGE_TYPES.HUB,
  CMS_PAGE_TYPES.LISTING,
  CMS_PAGE_TYPES.CMS_CONTENT,
  CMS_PAGE_TYPES.PUBLIC,
  CMS_PAGE_TYPES.CUSTOM,
];

const HUB_AND_CONTENT = [
  CMS_PAGE_TYPES.HUB,
  CMS_PAGE_TYPES.CMS_CONTENT,
  CMS_PAGE_TYPES.PUBLIC,
  CMS_PAGE_TYPES.CUSTOM,
];

// ═══════════════════════════════════════════════════
// THE REGISTRY — Single Source of Truth
// ═══════════════════════════════════════════════════
const REGISTRY = {

  // ── Visual / Decorative blocks ──
  banner: {
    label: 'Bannière',
    icon: 'Image',
    allowedPageTypes: ALL_CONTENT_PAGES,
    systemOnly: false,
    adminVisible: false, // superseded by banner_fixed_centered in admin UI
  },
  banner_fixed_centered: {
    label: 'Bannière Fixe Txt Centré',
    icon: 'Image',
    allowedPageTypes: ALL_CONTENT_PAGES,
    systemOnly: false,
    adminVisible: true,
  },
  carousel_banner: {
    label: 'Carousel Bannières',
    icon: 'Image',
    allowedPageTypes: HUB_AND_CONTENT,
    systemOnly: false,
    adminVisible: true,
  },
  text: {
    label: 'Texte',
    icon: 'Type',
    allowedPageTypes: ALL_CONTENT_PAGES,
    systemOnly: false,
    adminVisible: true,
  },

  // ── Layout blocks ──
  feature_grid: {
    label: 'Grille Features',
    icon: 'LayoutGrid',
    allowedPageTypes: HUB_AND_CONTENT,
    systemOnly: false,
    adminVisible: false, // replaced by modular_grid in admin
  },
  modular_grid: {
    label: 'Grille Modulaire',
    icon: 'LayoutGrid',
    allowedPageTypes: HUB_AND_CONTENT,
    systemOnly: false,
    adminVisible: true,
  },

  // ── Navigation blocks ──
  navigation_cards: {
    label: 'Cartes de Navigation',
    icon: 'Navigation',
    allowedPageTypes: HUB_AND_CONTENT,
    systemOnly: false,
    adminVisible: true,
  },

  // ── Data-driven blocks (async) ──
  product_list: {
    label: 'Liste de produits',
    icon: 'Package',
    allowedPageTypes: [CMS_PAGE_TYPES.CMS_CONTENT, CMS_PAGE_TYPES.PUBLIC, CMS_PAGE_TYPES.CUSTOM],
    systemOnly: false,
    adminVisible: true,
  },
  promotion_cards_grid: {
    label: 'Grille Cartes Promo (3 cols)',
    icon: 'Package',
    allowedPageTypes: HUB_AND_CONTENT,
    systemOnly: false,
    adminVisible: true,
  },
  partner_cards_grid: {
    label: 'Grille Cartes Partenaires (3 cols)',
    icon: 'Package',
    allowedPageTypes: HUB_AND_CONTENT,
    systemOnly: false,
    adminVisible: true,
  },
  blog_posts: {
    label: 'Articles Blog',
    icon: 'FileText',
    allowedPageTypes: HUB_AND_CONTENT,
    systemOnly: false,
    adminVisible: false, // managed via blog system
  },

  // ── Marketing blocks ──
  vitrin_block: {
    label: 'Vitrine Marketing',
    icon: 'Package',
    allowedPageTypes: ALL_CONTENT_PAGES,
    systemOnly: false,
    adminVisible: true,
  },

  // ── Special blocks ──
  thematic_page_card: {
    label: 'Carte Page Thématique',
    icon: 'FileText',
    allowedPageTypes: [CMS_PAGE_TYPES.HUB],
    systemOnly: false,
    adminVisible: false,
  },
  global_block_reference: {
    label: 'Bloc Global (référence)',
    icon: 'Link',
    allowedPageTypes: ALL_CONTENT_PAGES,
    systemOnly: false,
    adminVisible: false, // managed via global blocks system
  },
  offer: {
    label: 'Offre spéciale',
    icon: 'Gift',
    allowedPageTypes: HUB_AND_CONTENT,
    systemOnly: false,
    adminVisible: true,
  },
};

// ═══════════════════════════════════════════════════
// DERIVED FUNCTIONS — No parallel allowlists!
// ═══════════════════════════════════════════════════

/**
 * Get all block types allowed for a given page type.
 * This replaces HUB_ALLOWED_BLOCKS, LISTING_ALLOWED_BLOCKS,
 * and getAllowedCmsBlocks().
 */
export const getAllowedBlockTypes = (pageType) => {
  const normalizedType = pageType?.toUpperCase() || 'CUSTOM';
  return Object.keys(REGISTRY).filter(
    type => REGISTRY[type].allowedPageTypes.includes(normalizedType)
  );
};

/**
 * Check if a block type is allowed on a page type.
 * Used by filterAllowedBlocks in cmsOverlayService.
 */
export const isBlockAllowed = (blockType, pageType) => {
  const entry = REGISTRY[blockType];
  if (!entry) return false;
  const normalizedType = pageType?.toUpperCase() || 'CUSTOM';
  return entry.allowedPageTypes.includes(normalizedType);
};

/**
 * Get all registered block types (for BlockRenderer).
 */
export const getAllBlockTypes = () => Object.keys(REGISTRY);

/**
 * Get admin-visible block types (for AdminPageBlocks dropdown).
 */
export const getAdminBlockTypes = () => {
  return Object.entries(REGISTRY)
    .filter(([, cfg]) => cfg.adminVisible)
    .map(([type, cfg]) => ({
      value: type,
      label: cfg.label,
      iconName: cfg.icon,
    }));
};

/**
 * Get block config by type.
 */
export const getBlockConfig = (type) => REGISTRY[type] || null;

/**
 * Check if a block type is system-only (should not be placed via CMS).
 */
export const isSystemOnlyBlock = (type) => REGISTRY[type]?.systemOnly || false;

export default REGISTRY;
